/**
 * RegistryScrapingPanel — Scraping multi-registres gratuit.
 *
 * Permet de chercher dans :
 *   - Zefix (CH, via Tavily)
 *   - Companies House (UK, si clé)
 *   - SEC EDGAR (US, free)
 *   - EUR-Lex (EU, via Tavily)
 *
 * Cache 24h transparent côté serveur.
 * Historique des 20 dernières recherches.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import {
  Search, Loader2, Globe2, ExternalLink, History, Database, RefreshCw,
  Clock, ChevronDown, ChevronUp, FileText, AlertCircle, UserPlus, Check,
} from "lucide-react";

const JURIS_LABELS = {
  CH: { name: "Suisse", flag: "🇨🇭", color: "#E24B4A" },
  UK: { name: "Royaume-Uni", flag: "🇬🇧", color: "#378ADD" },
  US: { name: "États-Unis", flag: "🇺🇸", color: "#1D9E75" },
  EU: { name: "Union Européenne", flag: "🇪🇺", color: "#EF9F27" },
  FR: { name: "France", flag: "🇫🇷", color: "#7F77DD" },
};

const REGISTRY_LABELS = {
  zefix: "Zefix (CH)",
  geoadmin: "GeoAdmin / cadastre (CH)",
  fosc: "FOSC / SHAB (CH)",
  companies_house_uk: "Companies House (UK)",
  sec_edgar: "SEC EDGAR (US)",
  eurlex: "EUR-Lex (EU)",
  ted: "TED / JOUE (EU)",
  epo: "OEB / EPO brevets (EU)",
  boamp: "BOAMP marchés publics (FR)",
};

export default function RegistryScrapingPanel() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedJuris, setSelectedJuris] = useState(["CH", "US", "EU", "FR"]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedReg, setExpandedReg] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/scraping/registries/config`);
      setConfig(r.data);
    } catch { /* ignore */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/scraping/registries/history?limit=15`);
      setHistory(r.data?.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadConfig(); loadHistory(); }, [loadConfig, loadHistory]);

  const enabledJuris = useMemo(() => {
    if (!config?.registries) return ["CH", "US", "EU", "FR"];
    const map = {};
    Object.values(config.registries).forEach((r) => {
      if (r.enabled) map[r.jurisdiction] = true;
    });
    return Object.keys(map);
  }, [config]);

  const toggleJuris = (j) => {
    setSelectedJuris((prev) => prev.includes(j) ? prev.filter((x) => x !== j) : [...prev, j]);
  };

  const runSearch = async (forceRefresh = false, overrideQuery = null) => {
    const q = (overrideQuery ?? query).trim();
    if (q.length < 2) {
      notify.error(t("saas.registries.q_too_short", { defaultValue: "Saisis au moins 2 caractères." }));
      return;
    }
    if (selectedJuris.length === 0) {
      notify.error(t("saas.registries.no_juris", { defaultValue: "Sélectionne au moins une juridiction." }));
      return;
    }
    setBusy(true); setResults(null); setExpandedReg(null);
    try {
      const r = await api.post(`/workspace/scraping/registries/search`, {
        query: q,
        jurisdictions: selectedJuris,
        force_refresh: forceRefresh,
        max_per_registry: 8,
      });
      setResults(r.data);
      if (overrideQuery) setQuery(overrideQuery);
      loadHistory();
      if ((r.data?.total ?? 0) === 0) {
        notify.info(t("saas.registries.no_result", { defaultValue: "Aucun résultat trouvé." }));
      } else {
        notify.success(t("saas.registries.done", { defaultValue: "Recherche terminée." }));
      }
    } catch (e) {
      notify.error(fmtError(e, "Recherche échouée"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="noam-card p-5 space-y-4" data-testid="registry-scraping-panel">
      {/* Header */}
      <div>
        <div className="noam-overline inline-flex items-center gap-1.5">
          <Database className="w-3 h-3 text-[#378ADD]" />
          {t("saas.registries.overline", { defaultValue: "Scraping registres · mode 100% gratuit" })}
        </div>
        <h3 className="text-lg font-semibold text-white mt-1">
          {t("saas.registries.title", { defaultValue: "Registres publics multi-juridictions" })}
        </h3>
        <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
          {t("saas.registries.help", { defaultValue: "Recherche dans Zefix (CH), Companies House (UK), SEC EDGAR (US), EUR-Lex (EU). Cache 24h. Sources publiques uniquement." })}
        </p>
      </div>

      {/* Search bar */}
      <div className="space-y-2.5">
        <div className="flex gap-2 items-stretch flex-wrap">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) runSearch(false); }}
              placeholder={t("saas.registries.placeholder", { defaultValue: "Nom d'entité (ex: Nestlé, Apple, BNP Paribas)…" })}
              disabled={busy}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#378ADD]/40"
              data-testid="registry-query-input"
            />
          </div>
          <button
            onClick={() => runSearch(false)}
            disabled={busy || query.trim().length < 2}
            className="btn-primary text-xs px-4"
            data-testid="registry-search-btn"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            {busy ? t("saas.registries.searching", { defaultValue: "Recherche…" }) : t("saas.registries.search", { defaultValue: "Rechercher" })}
          </button>
          <button
            onClick={() => runSearch(true)}
            disabled={busy || query.trim().length < 2}
            className="btn-ghost text-xs px-3"
            title={t("saas.registries.force_refresh", { defaultValue: "Ignorer le cache 24h" })}
            data-testid="registry-force-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Juridiction toggles */}
        <div className="flex flex-wrap gap-1.5">
          {["CH", "UK", "US", "EU", "FR"].map((j) => {
            const enabled = enabledJuris.includes(j);
            const active = selectedJuris.includes(j);
            const meta = JURIS_LABELS[j];
            return (
              <button
                key={j}
                onClick={() => enabled && toggleJuris(j)}
                disabled={!enabled || busy}
                className={`text-[11px] px-2 py-1 rounded border transition ${
                  active
                    ? "bg-white/10 border-white/20 text-white"
                    : "bg-white/[0.02] border-white/5 text-zinc-500 hover:text-zinc-300"
                } ${!enabled ? "opacity-40 cursor-not-allowed line-through" : ""}`}
                title={!enabled ? "Clé manquante" : ""}
                data-testid={`registry-juris-${j}`}
              >
                <span className="mr-1">{meta.flag}</span>
                {meta.name}
              </button>
            );
          })}
          <button
            onClick={() => setShowHistory((s) => !s)}
            className="text-[11px] px-2 py-1 rounded border bg-white/[0.02] border-white/5 text-zinc-400 hover:text-white ml-auto inline-flex items-center gap-1"
            data-testid="registry-history-toggle"
          >
            <History className="w-3 h-3" />
            {t("saas.registries.history", { defaultValue: "Historique" })} ({history.length})
          </button>
        </div>
      </div>

      {/* History */}
      {showHistory && history.length > 0 && (
        <div className="bg-white/[0.02] border border-white/5 rounded-lg p-3 space-y-1.5 max-h-64 overflow-y-auto" data-testid="registry-history-list">
          {history.map((h) => (
            <button
              key={h.id}
              onClick={() => { setSelectedJuris(h.jurisdictions || ["CH", "US", "EU"]); runSearch(false, h.query); }}
              className="w-full text-left text-[11px] px-2 py-1.5 rounded hover:bg-white/5 flex items-center justify-between gap-2"
            >
              <span className="text-zinc-200 truncate flex-1 font-medium">{h.query}</span>
              <span className="text-zinc-500 shrink-0 text-[10px]">{h.total} • {(h.jurisdictions || []).join("·")}</span>
            </button>
          ))}
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-2.5" data-testid="registry-results">
          <div className="flex items-center justify-between text-[11px] text-zinc-500">
            <div className="inline-flex items-center gap-2">
              <span className="text-zinc-200 font-medium">{results.total}</span>
              <span>résultats pour</span>
              <span className="text-zinc-300">&quot;{results.query}&quot;</span>
            </div>
            <div className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {results.scanned_at ? new Date(results.scanned_at).toLocaleTimeString() : ""}
              {Object.values(results.from_cache || {}).some((v) => v) && (
                <span className="ml-1 px-1.5 py-0.5 rounded bg-[#1D9E75]/10 text-[#1D9E75] text-[9px] uppercase">cache</span>
              )}
            </div>
          </div>

          {Object.entries(results.results || {}).map(([reg, items]) => {
            const isOpen = expandedReg === reg;
            const fromCache = (results.from_cache || {})[reg];
            return (
              <div key={reg} className="bg-white/[0.02] border border-white/5 rounded-lg" data-testid={`registry-block-${reg}`}>
                <button
                  onClick={() => setExpandedReg(isOpen ? null : reg)}
                  className="w-full px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-white/[0.02]"
                  data-testid={`registry-toggle-${reg}`}
                >
                  <div className="flex items-center gap-2">
                    <Globe2 className="w-3.5 h-3.5 text-zinc-400" />
                    <span className="text-sm text-white font-medium">{REGISTRY_LABELS[reg] || reg}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-300">{items.length}</span>
                    {fromCache && <span className="text-[9px] px-1 py-0.5 rounded bg-[#1D9E75]/10 text-[#1D9E75] uppercase">cache</span>}
                  </div>
                  {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                </button>
                {isOpen && (
                  <div className="border-t border-white/5 p-3 space-y-2">
                    {items.length === 0 ? (
                      <div className="text-[11px] text-zinc-500 inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Aucun résultat dans ce registre.
                      </div>
                    ) : (
                      items.map((it, i) => <RegistryItem key={i} it={it} reg={reg} />)
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!results && !busy && (
        <div className="text-center py-6 text-[11px] text-zinc-500">
          {t("saas.registries.empty", { defaultValue: "Saisis le nom d'une entité pour rechercher dans les registres publics." })}
        </div>
      )}
    </div>
  );
}

function RegistryItem({ it, reg }) {
  const { t } = useTranslation();
  const isSec = reg === "sec_edgar";
  const isCH = reg === "companies_house_uk";
  const isBoamp = reg === "boamp";
  const [created, setCreated] = useState(false);
  const [busy, setBusy] = useState(false);

  // Map registry → annuaire defaults
  const buildEntityPayload = () => {
    const jurisdictionToCountry = { CH: "Suisse", UK: "Royaume-Uni", US: "États-Unis", EU: "Union Européenne", FR: "France" };
    const tags = [reg];
    if (it.form_type) tags.push(`SEC:${it.form_type}`);
    if (it.cik) tags.push(`CIK:${it.cik}`);
    if (it.uid) tags.push(`UID:${it.uid}`);
    if (it.chid) tags.push(`CHID:${it.chid}`);
    if (it.company_number) tags.push(`CompanyN°:${it.company_number}`);
    if (it.category) tags.push(it.category);

    const descParts = [];
    if (it.summary) descParts.push(it.summary);
    if (it.source_url) descParts.push(`Source : ${it.source_url}`);
    if (it.legal_form) descParts.push(`Forme juridique : ${it.legal_form}`);
    if (it.status || it.company_status) descParts.push(`Statut : ${it.status || it.company_status}`);
    if (it.address) descParts.push(`Adresse : ${it.address}`);
    if (it.date_of_creation) descParts.push(`Fondée : ${it.date_of_creation}`);
    if (it.filing_date) descParts.push(`Dépôt SEC : ${it.filing_date}`);
    if (it.publication_date) descParts.push(`Publication : ${it.publication_date}`);
    if (it.deadline) descParts.push(`Échéance : ${it.deadline}`);

    return {
      name: (it.name || "(Sans nom)").slice(0, 200),
      type: isBoamp ? "institution" : "societe",
      country: jurisdictionToCountry[it.jurisdiction] || it.jurisdiction || null,
      status: "prospect",
      description: descParts.join("\n").slice(0, 2000),
      tags,
      notes: `Importé depuis ${REGISTRY_LABELS[reg] || reg}`,
    };
  };

  const createEntity = async () => {
    setBusy(true);
    try {
      const payload = buildEntityPayload();
      await api.post(`/workspace/annuaire/entities`, payload);
      setCreated(true);
      notify.success(t("saas.registries.entity_created", { defaultValue: "Entité créée dans l'Annuaire." }));
    } catch (e) {
      notify.error(fmtError(e, t("saas.registries.entity_failed", { defaultValue: "Création échouée" })));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-md p-2.5 hover:border-white/10 transition" data-testid={`registry-item-${reg}-${it.name?.slice(0,12)}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="text-[12px] text-white font-medium leading-snug">{it.name || "(sans nom)"}</h4>
          <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[10px] text-zinc-500">
            {it.legal_form && <span className="px-1.5 py-0.5 rounded bg-white/5">{it.legal_form}</span>}
            {it.status && <span className="px-1.5 py-0.5 rounded bg-white/5">{it.status}</span>}
            {it.canton && <span>📍 {it.canton}</span>}
            {it.uid && <span>UID {it.uid}</span>}
            {it.chid && <span>CHID {it.chid}</span>}
            {isSec && it.form_type && <span className="px-1.5 py-0.5 rounded bg-[#378ADD]/10 text-[#378ADD]"><FileText className="w-2.5 h-2.5 inline mr-0.5" />{it.form_type}</span>}
            {isSec && it.filing_date && <span>{it.filing_date}</span>}
            {isSec && it.cik && <span>CIK {it.cik}</span>}
            {isCH && it.company_number && <span>N° {it.company_number}</span>}
            {isCH && it.company_status && <span>{it.company_status}</span>}
            {isBoamp && it.buyer && <span className="truncate">acheteur: {it.buyer}</span>}
            {isBoamp && it.publication_date && <span>{it.publication_date}</span>}
            {isBoamp && it.deadline && <span className="text-[#EF9F27]">échéance: {it.deadline}</span>}
            {isBoamp && it.category && <span className="px-1.5 py-0.5 rounded bg-[#7F77DD]/10 text-[#7F77DD]">{it.category}</span>}
            {it.address && <span className="truncate">{it.address}</span>}
            {it.date_of_creation && <span>fondée {it.date_of_creation}</span>}
          </div>
          {it.summary && (
            <p className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed line-clamp-2">{it.summary}</p>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          {it.source_url && (
            <a
              href={it.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-[#378ADD]"
              title={t("saas.registries.open_source", { defaultValue: "Ouvrir la source" })}
              data-testid="registry-item-link"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <button
            onClick={createEntity}
            disabled={busy || created}
            className={`text-[10px] px-2 py-1 rounded border inline-flex items-center gap-1 transition ${
              created
                ? "bg-[#1D9E75]/10 border-[#1D9E75]/40 text-[#1D9E75] cursor-default"
                : "bg-white/[0.02] border-white/10 text-zinc-400 hover:text-white hover:border-[#1D9E75]/40 disabled:opacity-50"
            }`}
            data-testid={`registry-item-create-entity-${reg}`}
            title={t("saas.registries.create_entity_tip", { defaultValue: "Créer une entité dans l'Annuaire avec ces infos" })}
          >
            {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
              : created ? <Check className="w-2.5 h-2.5" />
              : <UserPlus className="w-2.5 h-2.5" />}
            {busy
              ? t("saas.registries.creating", { defaultValue: "Création…" })
              : created
                ? t("saas.registries.created", { defaultValue: "Créée" })
                : t("saas.registries.create_entity", { defaultValue: "Créer entité" })}
          </button>
        </div>
      </div>
    </div>
  );
}
